import type {
  Location,
  CashFlowMonth,
  CashMovement,
  CostNature,
  DailyRevenue,
  ExpenseCategory,
  ExpenseEntry,
  Investment,
  InventoryCount,
  Kpis,
  LineaResultado,
  CambioPorReparto,
  Payable,
  PayableStatus,
  ProfitAndLoss,
  Target,
  TargetComparison,
  TenantSettings,
  WeekdayPerformance,
} from './types';
import { sePuedeDeber, totalAPagar } from './porPagar';
import { effectiveLocationId, foldInHouse, SHARED_LOCATION_ID, type SharedCostAllocation } from './types';
import { formatMoneyIn } from './currency';
import { existenciasDelPeriodo } from './inventory';
import { naturalezaDe, type AjusteNaturalezas } from './naturaleza';
import { round } from './units';
import { comieronAquiDe, resumenVentas } from './ventas';
// Bloque I (préstamos): intereses de préstamos al Estado de resultados.
import { interesesDePrestamos, parteDelMes, type Loan } from './loans';
import { addMonths, inPeriod, monthDiff, monthOf, monthsIn, Period, periodOfMonth } from './period';

/* ------------------------------------------------------------------ */
/* Plan de cuentas (literal de la plantilla, hoja Tabs C1:C22)         */
/* ------------------------------------------------------------------ */

/**
 * Etiquetas visibles del plan de cuentas.
 *
 * Desde la plantilla de gastos de Alex (oct 2026) son EXACTAMENTE las de su
 * hoja «Categorías» (decisión de Pol). Las CLAVES no cambian —el histórico
 * cuelga de ellas—: las de siempre cambian de nombre y hay cinco nuevas
 * (bebidas, empaque, otros_personal, limpieza, suministros).
 *
 * Las claves viejas que ya no están en la lista no se borran ni se migran:
 * dejan de ofrecerse y se enseñan con el nombre de su equivalente
 * (`LEGACY_CATEGORY_EQUIVALENT`). Por eso este Record sigue teniendo TODAS
 * las claves: la app móvil publicada y los datos viejos las siguen usando.
 */
export const CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  compras_materia_prima: 'Comida',
  bebidas: 'Bebidas',
  empaque: 'Empaque',
  otros_insumos: 'Otros insumos de cocina',
  sueldos_salarios: 'Sueldos',
  cuotas_ss: 'Seguridad social',
  otros_personal: 'Otros de personal',
  alquiler: 'Renta del local',
  suministros: 'Luz, agua y gas',
  telefonia: 'Teléfono e internet',
  reparaciones: 'Reparaciones y mantenimiento',
  limpieza: 'Limpieza y desechables',
  publicidad_rrss: 'Publicidad y redes',
  // «Comisiones (tarjeta y apps)» (revisión del 14 oct 2026): aquí van también
  // las de Uber Eats, Rappi, Glovo… (antes «Comisiones de plataformas»).
  comisiones_bancarias: 'Comisiones (tarjeta y apps)',
  honorarios: 'Contador y asesores',
  comunicacion_web: 'Software y suscripciones',
  seguros: 'Seguros',
  papeleria: 'Papelería y oficina',
  otros_gastos: 'Otros',
  intereses: 'Intereses y préstamos',
  impuesto_ganancias: 'Impuestos sobre la ganancia',
  // Claves viejas: se enseñan como su equivalente.
  suministros_agua: 'Luz, agua y gas',
  suministros_electricidad: 'Luz, agua y gas',
  suministros_gas: 'Luz, agua y gas',
  servicios_varios: 'Otros',
  uniformidad: 'Otros de personal',
  amortizaciones: 'Depreciaciones',
};

/**
 * Claves viejas → la categoría de la lista con la que se enseñan y se
 * suman en los informes. Al editar un gasto viejo se ofrece (y se guarda) la
 * nueva. `amortizaciones` no está: sigue siendo la depreciación, que la cuenta
 * de resultados calcula aparte desde las inversiones.
 */
export const LEGACY_CATEGORY_EQUIVALENT: Partial<Record<ExpenseCategory, ExpenseCategory>> = {
  suministros_agua: 'suministros',
  suministros_electricidad: 'suministros',
  suministros_gas: 'suministros',
  servicios_varios: 'otros_gastos',
  uniformidad: 'otros_personal',
};

/** La categoría con la que se enseña y se suma un gasto (la suya, o su equivalente si es vieja). */
export function visibleCategory(category: ExpenseCategory): ExpenseCategory {
  return LEGACY_CATEGORY_EQUIVALENT[category] ?? category;
}

/** Categorías de cada grupo, en el orden de la hoja «Categorías» de la plantilla. */
export const MATERIAL_CATEGORIES: ExpenseCategory[] = ['compras_materia_prima', 'bebidas', 'empaque', 'otros_insumos'];
export const LABOR_CATEGORIES: ExpenseCategory[] = ['sueldos_salarios', 'cuotas_ss', 'otros_personal'];
export const OPERATING_CATEGORIES: ExpenseCategory[] = [
  'alquiler',
  'suministros',
  'telefonia',
  'reparaciones',
  'limpieza',
  'publicidad_rrss',
  'comisiones_bancarias',
  'honorarios',
  'comunicacion_web',
  'seguros',
  'papeleria',
  'otros_gastos',
];

/**
 * Categorias que NO son gasto de explotacion: van por debajo del EBITDA
 * («Financieros» en la plantilla). `amortizaciones` cuenta aquí pero no se
 * ofrece: la depreciación sale de las inversiones.
 */
export const BELOW_EBITDA_CATEGORIES: ExpenseCategory[] = ['intereses', 'impuesto_ganancias'];

/** ¿Es de este grupo? Con las claves viejas, por su equivalente. */
export const isMaterialCategory = (c: ExpenseCategory): boolean => MATERIAL_CATEGORIES.includes(visibleCategory(c));
export const isLaborCategory = (c: ExpenseCategory): boolean => LABOR_CATEGORIES.includes(visibleCategory(c));

/**
 * Los 3 grupos de la reunion con Alex (+ el bloque bajo el EBITDA). Los
 * SUBGRUPOS personalizados del restaurante ("Carnes", "Cocina", "Sala"...)
 * cuelgan de uno de estos tres; el bloque financiero no admite subgrupos.
 */
export type ExpenseGroupKey = 'materia_prima' | 'personal' | 'generales';

export const EXPENSE_GROUP_LABELS: Record<ExpenseGroupKey, string> = {
  // Los nombres de la plantilla de gastos de Alex (oct 2026).
  materia_prima: 'Materia prima',
  personal: 'Personal',
  generales: 'Gastos generales',
};

export const EXPENSE_GROUP_KEYS = Object.keys(EXPENSE_GROUP_LABELS) as ExpenseGroupKey[];

/** Grupo de Alex al que pertenece una categoria (undefined = bajo el EBITDA). */
export function expenseGroupOf(category: ExpenseCategory): ExpenseGroupKey | undefined {
  const visible = visibleCategory(category);
  if (MATERIAL_CATEGORIES.includes(visible)) return 'materia_prima';
  if (LABOR_CATEGORIES.includes(visible)) return 'personal';
  if (OPERATING_CATEGORIES.includes(visible)) return 'generales';
  return undefined;
}

export interface SubgroupTotal {
  group: ExpenseGroupKey;
  /** undefined = gastos del grupo sin subgrupo asignado. */
  subgroup?: string;
  total: number;
  count: number;
}

/**
 * Desglose de gastos por subgrupo, dentro de cada grupo de Alex. Solo
 * cuenta los gastos de explotacion (lo de bajo el EBITDA no lleva
 * subgrupo). Orden: por grupo (materia prima, personal, generales) y
 * dentro de cada grupo por importe descendente, con "sin subgrupo" al final.
 */
export function expensesBySubgroup(
  expenses: Array<Pick<ExpenseEntry, 'category' | 'netAmount' | 'subgroup'>>,
): SubgroupTotal[] {
  const keyOf = (group: ExpenseGroupKey, subgroup?: string) => `${group} ${subgroup ?? ''}`;
  const map = new Map<string, SubgroupTotal>();
  for (const expense of expenses) {
    const group = expenseGroupOf(expense.category);
    if (!group) continue;
    const subgroup = expense.subgroup?.trim() || undefined;
    const key = keyOf(group, subgroup);
    const current = map.get(key) ?? { group, subgroup, total: 0, count: 0 };
    current.total = round(current.total + expense.netAmount, 2);
    current.count += 1;
    map.set(key, current);
  }
  const groupOrder: ExpenseGroupKey[] = ['materia_prima', 'personal', 'generales'];
  return [...map.values()].sort((a, b) => {
    if (a.group !== b.group) return groupOrder.indexOf(a.group) - groupOrder.indexOf(b.group);
    // "sin subgrupo" siempre al final del grupo
    if (!a.subgroup !== !b.subgroup) return a.subgroup ? -1 : 1;
    return b.total - a.total;
  });
}

/** Las categorías que se ofrecen, en el orden de la plantilla. Sin las claves viejas. */
export const CATEGORY_ORDER: ExpenseCategory[] = [
  ...MATERIAL_CATEGORIES,
  ...LABOR_CATEGORIES,
  ...OPERATING_CATEGORIES,
  ...BELOW_EBITDA_CATEGORIES,
];

/** Naturaleza sugerida al dar de alta un gasto. */
export const DEFAULT_NATURE: Record<ExpenseCategory, CostNature> = {
  compras_materia_prima: 'variable',
  bebidas: 'variable',
  empaque: 'variable',
  otros_insumos: 'variable',
  otros_personal: 'fijo',
  limpieza: 'variable',
  suministros: 'variable',
  sueldos_salarios: 'fijo',
  cuotas_ss: 'fijo',
  alquiler: 'fijo',
  reparaciones: 'variable',
  honorarios: 'fijo',
  seguros: 'fijo',
  comisiones_bancarias: 'variable',
  uniformidad: 'fijo',
  publicidad_rrss: 'fijo',
  comunicacion_web: 'fijo',
  suministros_agua: 'variable',
  suministros_electricidad: 'variable',
  suministros_gas: 'variable',
  servicios_varios: 'fijo',
  telefonia: 'fijo',
  papeleria: 'fijo',
  otros_gastos: 'fijo',
  amortizaciones: 'fijo',
  intereses: 'fijo',
  impuesto_ganancias: 'variable',
};

const sum = (values: number[]) => values.reduce((a, b) => a + b, 0);

export interface FinanceInput {
  locations?: Location[];
  revenues: DailyRevenue[];
  expenses: ExpenseEntry[];
  investments: Investment[];
  cashMovements?: CashMovement[];
  /** Recuentos mensuales de existencias; sin ellos la P&L usa solo compras. */
  inventoryCounts?: InventoryCount[];
  /** Bloque I: préstamos; sus intereses entran solos en la P&L. */
  loans?: Loan[];
}

/* ------------------------------------------------------------------ */
/* Ingresos                                                            */
/* ------------------------------------------------------------------ */

/** Facturacion bruta del dia, sin IVA: local + domicilio. */
export function revenueOf(day: DailyRevenue): number {
  return inHouseRevenueOf(day) + deliveryRevenueOf(day);
}

/**
 * Solo lo vendido en sala, sin IVA. Es la base de los tickets promedio: un
 * pedido a domicilio no es una mesa ni un comensal contado (un mismo pedido
 * puede ser para varios), y meter esa facturacion los inflaba. Lo pidio un
 * founder a traves de Alex el 9 sep 2026, al ver el de la pestaña de
 * ingresos; el panel ya lo hacia asi y la pestaña, la rejilla diaria y el
 * rendimiento por dia de la semana no.
 */
export function inHouseRevenueOf(day: DailyRevenue): number {
  return sum(Object.values(day.inHouse ?? {}));
}

/** Solo lo vendido a domicilio, todas las plataformas juntas. */
export function deliveryRevenueOf(day: DailyRevenue): number {
  return sum(Object.values(day.delivery ?? {}));
}

/**
 * Facturacion del dia que entra en la cuenta de resultados.
 *
 * OJO: no descuenta cortesias ni descuentos, y no es un descuido. En la
 * plantilla, "TOTAL FACTURACION" de la hoja de ingresos es
 * =SUM(C9;C14) -> en el local + a domicilio; Cortesias (fila 31) y Descuentos
 * (fila 32) van aparte y no entran en ninguna suma. Se comprueba con los datos
 * de ejemplo: el desglose por metodo de pago (872+153 = 1025) y el desglose
 * por tipo de producto (698+273+0+54 = 1025) dan el mismo total, y las
 * cortesias (4) y descuentos (20) del mismo dia quedan fuera de los dos.
 * Lo que se teclea en cada metodo de cobro es el dinero realmente cobrado, ya
 * neto de descuento; una cortesia no genero ingreso nunca. Restarlos seria
 * contarlos dos veces.
 *
 * Se mantiene la funcion (la usan el API y la rejilla de ingresos) para dejar
 * explicito cual es el criterio.
 */
export function netRevenueOf(day: DailyRevenue): number {
  return revenueOf(day);
}

export function revenueByPaymentMethod(days: DailyRevenue[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const d of days) {
    // foldInHouse funde debito+credito antiguos en 'tarjeta' (unificación de
    // Alex): el desglose siempre sale con los métodos nuevos.
    for (const [method, value] of Object.entries(foldInHouse(d.inHouse))) {
      out[method] = (out[method] ?? 0) + value;
    }
  }
  return out;
}

export function revenueByPlatform(days: DailyRevenue[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const d of days) {
    for (const [platformId, value] of Object.entries(d.delivery ?? {})) {
      out[platformId] = (out[platformId] ?? 0) + value;
    }
  }
  return out;
}

const WEEKDAY_LABELS = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];

/**
 * Rendimiento por dia de la semana. Responde a "¿me compensa abrir los lunes?",
 * que es la pregunta que mas veces se hace un duenyo de restaurante.
 */
export function weekdayPerformance(days: DailyRevenue[], period: Period): WeekdayPerformance[] {
  const buckets = WEEKDAY_LABELS.map((label, i) => ({
    weekday: i + 1,
    label,
    revenue: 0,
    inHouseRevenue: 0,
    customers: 0,
    averageTicket: 0,
    days: 0,
  }));

  for (const d of days) {
    if (!inPeriod(d.date, period)) continue;
    // getUTCDay: 0 = domingo -> lo movemos a 6 para que la semana empiece en lunes
    const jsDay = new Date(`${d.date}T00:00:00Z`).getUTCDay();
    const index = jsDay === 0 ? 6 : jsDay - 1;
    const bucket = buckets[index];
    bucket.revenue += revenueOf(d);
    // Ticket: solo quien comió aquí y solo los días con clientes.
    if ((d.customers ?? 0) > 0) {
      bucket.inHouseRevenue += comieronAquiDe(d);
      bucket.customers += d.customers ?? 0;
    }
    bucket.days += 1;
  }

  // Las barras llevan la venta entera; el ticket, solo la del local (ver
  // `inHouseRevenueOf`), el mismo criterio que el KPI del panel.
  return buckets.map(({ inHouseRevenue, ...b }) => ({
    ...b,
    revenue: round(b.revenue, 2),
    averageTicket: b.customers > 0 ? round(inHouseRevenue / b.customers, 2) : 0,
  }));
}

/* ------------------------------------------------------------------ */
/* Amortizaciones                                                      */
/* ------------------------------------------------------------------ */

export function investmentAmount(inv: Investment): number {
  return (inv.quantity ?? 1) * inv.unitPrice;
}

/**
 * Amortizacion lineal, igual que la hoja "Inversiones":
 *
 *   G Total               = E x F                (importe unitario x cantidad)
 *   J Amortizacion anual  = G / I                (I = vida util en anyos)
 *   K Amortizacion mensual= (G / I) / 12
 */
export function monthlyDepreciation(inv: Investment): number {
  if (!inv.usefulLifeYears || inv.usefulLifeYears <= 0) return 0;
  return investmentAmount(inv) / (inv.usefulLifeYears * 12);
}

export function depreciationForMonth(inv: Investment, month: string): number {
  // Una inversión «de todos» en la cuenta de un local: su parte de ESTE mes.
  return depreciacionEntera(inv, month) * parteDelMes(inv, month);
}

function depreciacionEntera(inv: Investment, month: string): number {
  const start = monthOf(inv.date);
  if (month < start) return 0;
  // Bloque I: «Ya no lo tengo» — deja de contar desde el mes en que se fue.
  if (inv.disposedOn && month >= monthOf(inv.disposedOn)) return 0;
  if (!inv.usefulLifeYears || inv.usefulLifeYears <= 0) {
    return month === start ? investmentAmount(inv) : 0;
  }
  const elapsed = monthDiff(start, month);
  return elapsed < inv.usefulLifeYears * 12 ? monthlyDepreciation(inv) : 0;
}

export function depreciationForPeriod(investments: Investment[], period: Period): number {
  const months = monthsIn(period);
  return sum(investments.flatMap((inv) => months.map((m) => depreciationForMonth(inv, m))));
}

/**
 * Cuadro de amortizacion: cuanto queda por amortizar de cada inversion.
 *
 * DIFERENCIA DELIBERADA CON LA PLANTILLA. Excel trabaja en anyos enteros:
 *   C "Anyo de vida"      = anyoActual + 1 - anyoAdquisicion
 *   L "Saldo por amortizar" = G - (J x C)
 * o sea que una maquina comprada en diciembre carga un anyo completo de
 * amortizacion en ese mismo ejercicio, y la fila 37 del P&L imputa
 * -Inversiones!$I$6 (el total mensual de TODAS las inversiones) a los doce
 * meses, tambien a los anteriores a la compra. Aqui se prorratea por meses
 * desde la fecha real de alta y se corta al agotar la vida util: el mismo
 * metodo lineal, pero sin descuadrar los meses. A cierre de ejercicio de una
 * compra de enero los dos dan el mismo numero.
 */
export type AmortizationStatus = 'futura' | 'amortizando' | 'terminada';

export function amortizationSchedule(investments: Investment[], asOfMonth: string) {
  return investments.map((inv) => {
    const total = investmentAmount(inv);
    const start = monthOf(inv.date);
    const monthly = monthlyDepreciation(inv);
    const vidaMeses = (inv.usefulLifeYears || 0) * 12;
    const empezada = asOfMonth >= start;
    let transcurridos = empezada ? monthDiff(start, asOfMonth) + 1 : 0;
    // «Ya no lo tengo»: desde el mes de la baja ya no se desgasta nada más.
    const baja = inv.disposedOn ? monthOf(inv.disposedOn) : undefined;
    const dadaDeBaja = Boolean(baja && empezada && asOfMonth >= baja);
    if (baja) transcurridos = Math.min(transcurridos, Math.max(0, monthDiff(start, baja)));
    const elapsed = Math.max(0, Math.min(transcurridos, vidaMeses));

    /*
     * Una inversion "al gasto" (sin vida util) se imputa entera el mes de la
     * compra — pero NO antes. Sin la comprobacion de `empezada`, una compra de
     * diciembre salia 100 % amortizada al consultar el cuadro de enero.
     */
    const accumulated = vidaMeses > 0 ? monthly * elapsed : empezada && transcurridos > 0 ? total : 0;

    /*
     * Cuota del MES CONSULTADO, que es lo unico que tiene sentido enseñar en
     * un cuadro con selector de mes: 0 si la compra aun no ha llegado y 0 si
     * la vida util ya se agoto. `monthlyDepreciation` es la cuota teorica y
     * no sabe nada de fechas: usarla tal cual hacia que un horno de hace diez
     * anyos siguiera "amortizando" para siempre.
     */
    const monthlyNow = depreciationForMonth(inv, asOfMonth);

    const status: AmortizationStatus = !empezada
      ? 'futura'
      : dadaDeBaja || (vidaMeses > 0 && elapsed >= vidaMeses)
        ? 'terminada'
        : vidaMeses > 0
          ? 'amortizando'
          : 'terminada'; // gasto directo: se agota el mismo mes

    return {
      investmentId: inv.investmentId,
      concept: inv.concept,
      date: inv.date,
      usefulLifeYears: inv.usefulLifeYears,
      totalAmount: round(total, 2),
      /** Cuota teorica del plan (constante durante toda la vida util). */
      monthlyDepreciation: round(monthly, 2),
      annualDepreciation: round(monthly * 12, 2),
      /** Lo que realmente carga ESTE mes. 0 si no toca. */
      monthlyNow: round(monthlyNow, 2),
      annualNow: round(monthlyNow * 12, 2),
      accumulated: round(Math.min(accumulated, total), 2),
      pending: dadaDeBaja ? 0 : round(Math.max(total - accumulated, 0), 2),
      status,
    };
  });
}

/* ------------------------------------------------------------------ */
/* Cuenta de resultados                                                */
/* ------------------------------------------------------------------ */

/**
 * Cascada exacta de la hoja "Cuenta de resultados (full)":
 *
 *   INGRESOS   =SUM(C7:C10)                (las 4 lineas de ingresos)
 *   Cortesias  (fila 11)  \ informativas: se muestran con su % sobre
 *   Descuentos (fila 12)  / ingresos pero NO entran en la cascada
 *   - Coste de materia prima               (compras + otros insumos)
 *   - Coste de personal                    (sueldos + SS)
 *     -> PRIME COST
 *   - Gastos generales de explotacion      (15 categorias)
 *     = EBITDA
 *   - Amortizaciones                       (desde inversiones)
 *     = EBIT
 *   - Intereses
 *     = EBT
 *   - Impuesto sobre las ganancias
 *     = BENEFICIO NETO
 *
 * PRIME COST: en la plantilla la fila 19 es =-(C13+C16)/C6, es decir un
 * PORCENTAJE sobre ingresos, no un importe. Devolvemos los dos
 * (`primeCost` y `primeCostPct`); `primeCostPct` es el numero de Alex.
 *
 * Todos los porcentajes van sobre INGRESOS (=IFERROR(Cxx/$C$6,"")).
 */
export function computeProfitAndLoss(input: FinanceInput, period: Period): ProfitAndLoss {
  const days = input.revenues.filter((d) => inPeriod(d.date, period));
  const enPeriodo = input.expenses.filter((e) => inPeriod(e.date, period));
  // Bloque R (regla 8 de pagos repartidos): el IVA que se pasa a Hacienda y
  // las retenciones se guardan en Gastos, pero no son gasto.
  const expenses = enPeriodo.filter((e) => !e.noEsGasto);

  const revenue = sum(days.map(revenueOf));
  const courtesies = sum(days.map((d) => d.courtesies ?? 0));
  const discounts = sum(days.map((d) => d.discounts ?? 0));

  // Cortesias y descuentos NO restan (ver netRevenueOf). La plantilla los
  // presenta debajo de INGRESOS con su % sobre ventas para que el duenyo vea
  // cuanto esta regalando, pero la cascada arranca de INGRESOS a secas:
  // EBITDA = C6 + C13 + C16 + C20, sin las filas 11 y 12.
  // Se conserva `netRevenue` como base de la cascada y de todos los
  // porcentajes (identico a `revenue`) para no romper el API ni el frontend.
  const netRevenue = revenue;

  const byCategory = Object.fromEntries(
    CATEGORY_ORDER.map((c) => [c, 0]),
  ) as Record<ExpenseCategory, number>;
  // Las claves viejas suman en la línea de su equivalente (suministros_agua
  // en «Luz, agua y gas»…): el informe enseña las categorías de la plantilla.
  for (const e of expenses) {
    const c = visibleCategory(e.category);
    byCategory[c] = (byCategory[c] ?? 0) + e.netAmount;
  }

  const totalOf = (categories: ExpenseCategory[]) => sum(categories.map((c) => byCategory[c] ?? 0));

  const materialCost = totalOf(MATERIAL_CATEGORIES);
  const laborCost = totalOf(LABOR_CATEGORIES);
  const operatingExpenses = totalOf(OPERATING_CATEGORIES);
  // Bloque I: los intereses de los préstamos van solos («Intereses de
  // préstamos»), sumados a los que se hayan registrado a mano en Gastos.
  const loanInterest = sum(monthsIn(period).map((m) => interesesDePrestamos(input.loans, m)));
  const financialExpenses = (byCategory.intereses ?? 0) + loanInterest;
  const taxes = byCategory.impuesto_ganancias ?? 0;

  /*
   * Consumo real de materia prima cuando hay recuentos de inventario:
   * iniciales + compras − finales. Comprar mucho un mes ya no hunde la P&L
   * si la mitad sigue en el almacen — que era justo la queja: "solo sale lo
   * negativo". Sin recuentos, compras a secas, como siempre.
   */
  // Sin recuento de fin de mes NO se pone 0: se usa el último recuento
  // conocido y se dice (bloque R, ver `existenciasDelPeriodo`).
  const localesDelAmbito = input.locations?.filter((l) => l.active !== false && l.locationId !== SHARED_LOCATION_ID).map((l) => l.locationId);
  const existencias = existenciasDelPeriodo(input.inventoryCounts ?? [], period, localesDelAmbito?.length ? localesDelAmbito : undefined);
  const inv = { hasInventory: existencias.hasInventory, opening: existencias.inicial.valor, closing: existencias.final.valor };
  const materialConsumption = inv.hasInventory
    ? materialCost + inv.opening - inv.closing
    : materialCost;

  // La amortizacion se calcula desde las inversiones; si ademas hay gastos
  // cargados a mano en esa categoria, se suman (algunos las llevan asi).
  const depreciation = depreciationForPeriod(input.investments, period) + (byCategory.amortizaciones ?? 0);

  const grossProfit = netRevenue - materialConsumption;
  const primeCost = materialConsumption + laborCost;
  const ebitda = netRevenue - materialConsumption - laborCost - operatingExpenses;
  const ebit = ebitda - depreciation;
  const ebt = ebit - financialExpenses;
  const netProfit = ebt - taxes;

  const pct = (value: number) => (netRevenue > 0 ? round((value / netRevenue) * 100, 2) : 0);

  return {
    periodStart: period.start,
    periodEnd: period.end,
    revenue: round(revenue, 2),
    courtesies: round(courtesies, 2),
    discounts: round(discounts, 2),
    netRevenue: round(netRevenue, 2),
    materialCost: round(materialCost, 2),
    hasInventory: inv.hasInventory,
    openingInventory: round(inv.opening, 2),
    closingInventory: round(inv.closing, 2),
    materialConsumption: round(materialConsumption, 2),
    laborCost: round(laborCost, 2),
    primeCost: round(primeCost, 2),
    primeCostPct: pct(primeCost),
    grossProfit: round(grossProfit, 2),
    grossMarginPct: pct(grossProfit),
    operatingExpenses: round(operatingExpenses, 2),
    ebitda: round(ebitda, 2),
    ebitdaPct: pct(ebitda),
    depreciation: round(depreciation, 2),
    ebit: round(ebit, 2),
    financialExpenses: round(financialExpenses, 2),
    ebt: round(ebt, 2),
    taxes: round(taxes, 2),
    netProfit: round(netProfit, 2),
    netProfitPct: pct(netProfit),
    byCategory: Object.fromEntries(
      Object.entries(byCategory).map(([k, v]) => [k, round(v, 2)]),
    ) as Record<ExpenseCategory, number>,
    // Desgloses para la cuenta de resultados desplegable (feedback 3 de Alex).
    revenueByMethod: roundRecord(revenueByPaymentMethod(days)),
    revenueByPlatform: roundRecord(revenueByPlatform(days)),
    subgroups: expensesBySubgroup(expenses),
    loanInterest: round(loanInterest, 2),
    existencias: { inicial: existencias.inicial, final: existencias.final },
    lineas: lineasDelResultado(expenses),
    cambiosPorRepartos: cambiosPorRepartos(expenses, period),
    fueraDelResultado: round(sum(enPeriodo.filter((e) => e.noEsGasto).map((e) => e.netAmount)), 2),
  };
}

function roundRecord(record: Record<string, number>): Record<string, number> {
  return Object.fromEntries(Object.entries(record).map(([k, v]) => [k, round(v, 2)]));
}

export function monthlyProfitAndLoss(input: FinanceInput, period: Period): ProfitAndLoss[] {
  return monthsIn(period).map((m) => computeProfitAndLoss(input, periodOfMonth(m)));
}

/* ------------------------------------------------------------------ */
/* Cuentas por pagar                                                   */
/* ------------------------------------------------------------------ */

export function payableStatus(expense: ExpenseEntry, today: string): PayableStatus {
  if (expense.paid) return 'pagada';
  if (!expense.dueDate) return 'por_vencer';
  return expense.dueDate >= today ? 'por_vencer' : 'vencida';
}

/**
 * Deudas a credito pendientes de pago. Es la parte de la plantilla que evita
 * el susto de descubrir una factura vencida cuando llama el proveedor.
 */
export function payables(expenses: ExpenseEntry[], today: string): Payable[] {
  return expenses
    // Una sola regla de lo que se debe (14 oct 2026): ver porPagar.ts.
    .filter((e) => sePuedeDeber(e))
    .map((e) => {
      const status = payableStatus(e, today);
      const daysOverdue =
        status === 'vencida' && e.dueDate
          ? Math.floor(
              (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${e.dueDate}T00:00:00Z`)) / 86_400_000,
            )
          : 0;
      return {
        entryId: e.entryId,
        concept: e.concept,
        supplierId: e.supplierId,
        invoiceNumber: e.invoiceNumber,
        dueDate: e.dueDate ?? '',
        // Con IVA, el total real de la factura (antes base por tipo: no cuadraba con la ficha).
        grossAmount: totalAPagar(e),
        status,
        daysOverdue,
      };
    })
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
}

export function totalDue(expenses: ExpenseEntry[], today: string): number {
  return round(sum(payables(expenses, today).map((p) => p.grossAmount)), 2);
}

/* ------------------------------------------------------------------ */
/* Flujo de caja                                                       */
/* ------------------------------------------------------------------ */

const gross = (net: number, vatPct: number) => net * (1 + (vatPct ?? 0) / 100);

/**
 * Flujo de caja de tesoreria con la estructura de la plantilla: bloque de
 * cobros, bloque de pagos, flujo economico, bloque financiero y saldo
 * acumulado. Usa fechas de COBRO y PAGO reales, no de devengo, e importes
 * con IVA porque el dinero entra y sale con IVA.
 *
 * Bloques y formulas de la hoja "Flujo de caja":
 *   INGRESOS (11-15)  ventas bancos | ventas efectivo | inversiones |
 *                     financiacion y aportes | otros ingresos
 *   GASTOS   (18-20)  operacion | impuestos | otros
 *   C22 FLUJO ECONOMICO  = C10 - C17
 *   C24 FINANCIERO       = C25 - C26        (ingresos - gastos financieros)
 *   C28 FLUJO FINANCIERO = $C$6 + SUM($C$22:C22) + SUM($C$24:C24)
 *                        = saldo inicial + acumulado economico + acumulado
 *                          financiero, que es exactamente el `closingBalance`
 *                          encadenado de este bucle.
 *
 * `vatBalance` separa el IVA cobrado y aun no liquidado: la causa numero uno
 * de que un restaurante "con caja" se quede sin dinero el dia de la
 * declaracion trimestral.
 */
export function computeCashFlow(
  input: FinanceInput,
  period: Period,
  settings: TenantSettings,
  openingBalance = 0,
): CashFlowMonth[] {
  const months = monthsIn(period);
  const index = new Map<string, CashFlowMonth>();

  for (const month of months) {
    index.set(month, {
      month,
      salesBank: 0,
      salesCash: 0,
      investmentIncome: 0,
      funding: 0,
      otherIncome: 0,
      totalInflow: 0,
      operatingPayments: 0,
      taxPayments: 0,
      otherPayments: 0,
      totalOutflow: 0,
      economicFlow: 0,
      financialIncome: 0,
      financialExpenses: 0,
      financialFlow: 0,
      openingBalance: 0,
      closingBalance: 0,
      vatBalance: 0,
      balanceExcludingVat: 0,
    });
  }

  // El IVA de cada venta es el de SU local (si la cuenta vende distinto en
  // cada local; si no, el de la cuenta), como en «ajustesDeVenta».
  const localPorId = new Map((input.locations ?? []).map((l) => [l.locationId, l]));
  const ivaDeVenta = (d: DailyRevenue): number => {
    const propio = settings.salesPerLocation ? localPorId.get(effectiveLocationId(d.locationId))?.sales?.vatPct : undefined;
    return propio ?? settings.vatPct ?? 0;
  };

  for (const d of input.revenues) {
    const row = index.get(monthOf(d.date));
    if (!row) continue;
    const vatPct = ivaDeVenta(d);
    const cash = d.inHouse?.efectivo ?? 0;
    const bank = revenueOf(d) - cash;
    row.salesCash += gross(cash, vatPct);
    row.salesBank += gross(bank, vatPct);
    row.vatBalance += revenueOf(d) * (vatPct / 100);
  }

  for (const e of input.expenses) {
    // Sin fecha de pago y sin marcar como pagado, todavia no sale de caja.
    const date = e.paymentDate ?? (e.paid ? e.date : undefined);
    if (!date) continue;
    const row = index.get(monthOf(date));
    if (!row) continue;
    if (e.category === 'amortizaciones') continue; // no es salida de caja

    const amount = gross(e.netAmount, e.vatPct);
    if (e.category === 'impuesto_ganancias') row.taxPayments += amount;
    else if (e.category === 'intereses') row.financialExpenses += amount;
    else row.operatingPayments += amount;

    row.vatBalance -= e.netAmount * ((e.vatPct ?? 0) / 100);
  }

  for (const inv of input.investments) {
    const row = index.get(monthOf(inv.date));
    if (!row) continue;
    const parte = parteDelMes(inv, monthOf(inv.date));
    row.otherPayments += gross(investmentAmount(inv) * parte, inv.vatPct);
    row.vatBalance -= investmentAmount(inv) * parte * ((inv.vatPct ?? 0) / 100);
  }

  for (const m of input.cashMovements ?? []) {
    const row = index.get(monthOf(m.date));
    if (!row) continue;
    switch (m.kind) {
      case 'ingresos_inversiones': row.investmentIncome += m.amount; break;
      case 'financiacion_aportes': row.funding += m.amount; break;
      case 'otros_ingresos': row.otherIncome += m.amount; break;
      case 'pago_impuestos': row.taxPayments += m.amount; break;
      case 'otros_gastos': row.otherPayments += m.amount; break;
      case 'ingresos_financieros': row.financialIncome += m.amount; break;
      case 'gastos_financieros': row.financialExpenses += m.amount; break;
    }
  }

  const result: CashFlowMonth[] = [];
  let balance = openingBalance;
  let cumulativeVat = 0;

  for (const month of months) {
    const row = index.get(month)!;
    // Se redondea ANTES de acumular: la columna de saldo tiene que cuadrar
    // sumando los numeros que el duenyo ve en pantalla, no los internos.
    for (const key of [
      'salesBank', 'salesCash', 'investmentIncome', 'funding', 'otherIncome',
      'operatingPayments', 'taxPayments', 'otherPayments',
      'financialIncome', 'financialExpenses', 'vatBalance',
    ] as const) {
      row[key] = round(row[key], 2);
    }

    row.totalInflow = round(row.salesBank + row.salesCash + row.investmentIncome + row.funding + row.otherIncome, 2);
    row.totalOutflow = round(row.operatingPayments + row.taxPayments + row.otherPayments, 2);
    row.economicFlow = round(row.totalInflow - row.totalOutflow, 2);
    row.financialFlow = round(row.financialIncome - row.financialExpenses, 2);

    row.openingBalance = round(balance, 2);
    balance = round(balance + row.economicFlow + row.financialFlow, 2);
    row.closingBalance = balance;

    cumulativeVat += row.vatBalance;
    row.balanceExcludingVat = round(balance - Math.max(cumulativeVat, 0), 2);

    result.push(row);
  }

  return result;
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/** "2026-02" -> "febrero de 2026". Un aviso no se lee en formato ISO. */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const nombre = MESES[m - 1] ?? month;
  return `${nombre} de ${y}`;
}

/**
 * Importe formateado en la moneda del tenant (locale y decimales de la
 * moneda: 1.234,56 € pero $12.500 en Chile). Delegado en currency.ts.
 */
export function formatMoney(value: number, currency = 'EUR'): string {
  return formatMoneyIn(value, currency);
}

export function cashAlerts(flow: CashFlowMonth[], currency = 'EUR'): string[] {
  const alerts: string[] = [];
  for (const m of flow) {
    if (m.closingBalance < 0) {
      alerts.push(
        `En ${monthLabel(m.month)} la caja se queda en ${formatMoney(m.closingBalance, currency)}.`,
      );
    } else if (m.balanceExcludingVat < 0) {
      alerts.push(
        `En ${monthLabel(m.month)} la caja solo aguanta porque incluye IVA sin liquidar: ` +
          `descontándolo te quedan ${formatMoney(m.balanceExcludingVat, currency)}.`,
      );
    }
  }
  return alerts;
}

/** Proyeccion simple a N meses con la media de los ultimos tres. */
export function projectCashFlow(history: CashFlowMonth[], monthsAhead: number): CashFlowMonth[] {
  if (history.length === 0 || monthsAhead <= 0) return [];
  const window = history.slice(-3);
  const avgIn = sum(window.map((m) => m.totalInflow)) / window.length;
  const avgOut = sum(window.map((m) => m.totalOutflow)) / window.length;

  const out: CashFlowMonth[] = [];
  let balance = history[history.length - 1].closingBalance;
  let month = history[history.length - 1].month;

  for (let i = 0; i < monthsAhead; i++) {
    month = addMonths(month, 1);
    const opening = balance;
    balance += avgIn - avgOut;
    out.push({
      month,
      salesBank: round(avgIn, 2),
      salesCash: 0,
      investmentIncome: 0,
      funding: 0,
      otherIncome: 0,
      totalInflow: round(avgIn, 2),
      operatingPayments: round(avgOut, 2),
      taxPayments: 0,
      otherPayments: 0,
      totalOutflow: round(avgOut, 2),
      economicFlow: round(avgIn - avgOut, 2),
      financialIncome: 0,
      financialExpenses: 0,
      financialFlow: 0,
      openingBalance: round(opening, 2),
      closingBalance: round(balance, 2),
      vatBalance: 0,
      balanceExcludingVat: round(balance, 2),
    });
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* KPIs                                                                */
/* ------------------------------------------------------------------ */

export function computeKpis(input: FinanceInput, period: Period, settings?: AjusteNaturalezas): Kpis {
  const pnl = computeProfitAndLoss(input, period);
  const days = input.revenues.filter((d) => inPeriod(d.date, period));

  const netRevenue = pnl.netRevenue;
  // Tickets con las reglas de Ventas (shared/ventas.ts): solo quien comió
  // aquí, y solo los días que tienen ese dato. Pedidos = para llevar + apps.
  const ventas = resumenVentas(days);
  const customers = ventas.clientes;
  const tables = ventas.mesas;
  const deliveryOrders = ventas.pedidos;

  // Bloque R: fijo/variable por CATEGORÍA y con las cifras del Estado de
  // resultados (consumo de materia prima, desgaste, intereses), para que el
  // equilibrio cuadre con la ganancia neta. Antes sumaba las compras tal
  // cual con el fijo/variable de cada gasto y no cuadraba nunca.
  const { fijos: fixedCosts, variables: variableCosts } = fijosYVariables(pnl, settings);

  const contributionMarginPct =
    netRevenue > 0 ? ((netRevenue - variableCosts) / netRevenue) * 100 : 0;
  const breakEvenRevenue = contributionMarginPct > 0 ? fixedCosts / (contributionMarginPct / 100) : 0;
  const safetyMarginPct =
    netRevenue > 0 && breakEvenRevenue > 0
      ? ((netRevenue - breakEvenRevenue) / netRevenue) * 100
      : 0;

  const totalCosts = fixedCosts + variableCosts;
  const pct = (value: number) => (netRevenue > 0 ? round((value / netRevenue) * 100, 2) : 0);

  return {
    periodStart: period.start,
    periodEnd: period.end,
    revenue: pnl.revenue,
    netRevenue,
    // Con inventario, el food cost es sobre CONSUMO, no sobre compras.
    foodCostPct: pct(pnl.materialConsumption),
    laborCostPct: pct(pnl.laborCost),
    primeCostPct: pnl.primeCostPct,
    ebitdaPct: pnl.ebitdaPct,
    customers,
    tables,
    averageTicketPerCustomer: ventas.cadaCliente ?? 0,
    averageTicketPerTable: ventas.porMesa ?? 0,
    deliveryOrders,
    averageTicketPerDeliveryOrder: ventas.porPedido ?? 0,
    breakEvenRevenue: round(breakEvenRevenue, 2),
    contributionMarginPct: round(contributionMarginPct, 2),
    safetyMarginPct: round(safetyMarginPct, 2),
    fixedCosts: round(fixedCosts, 2),
    variableCosts: round(variableCosts, 2),
    fixedCostPct: totalCosts > 0 ? round((fixedCosts / totalCosts) * 100, 2) : 0,
    variableCostPct: totalCosts > 0 ? round((variableCosts / totalCosts) * 100, 2) : 0,
    materialCost: pnl.materialConsumption,
  };
}

export interface KpiBenchmark {
  key: keyof Kpis;
  label: string;
  value: number;
  good: number;
  warning: number;
  lowerIsBetter: boolean;
  status: 'bien' | 'atencion' | 'mal';
}

/** Semaforo con los rangos de referencia del sector. Orientativos. */
export function benchmarkKpis(kpis: Kpis): KpiBenchmark[] {
  const defs: Omit<KpiBenchmark, 'value' | 'status'>[] = [
    { key: 'foodCostPct', label: 'Costo de materia prima', good: 32, warning: 38, lowerIsBetter: true },
    { key: 'laborCostPct', label: 'Costo de personal', good: 30, warning: 35, lowerIsBetter: true },
    { key: 'primeCostPct', label: 'Prime Cost', good: 60, warning: 70, lowerIsBetter: true },
    { key: 'ebitdaPct', label: 'EBITDA', good: 15, warning: 8, lowerIsBetter: false },
    { key: 'safetyMarginPct', label: 'Margen de seguridad', good: 20, warning: 5, lowerIsBetter: false },
  ];

  return defs.map((def) => {
    const value = kpis[def.key] as number;
    const status: KpiBenchmark['status'] = def.lowerIsBetter
      ? value <= def.good
        ? 'bien'
        : value <= def.warning
          ? 'atencion'
          : 'mal'
      : value >= def.good
        ? 'bien'
        : value >= def.warning
          ? 'atencion'
          : 'mal';
    return { ...def, value, status };
  });
}

/* ------------------------------------------------------------------ */
/* Objetivos                                                           */
/* ------------------------------------------------------------------ */

const TARGET_METRICS: Array<{ metric: keyof Target; label: string; from: (k: Kpis) => number }> = [
  // "Ventas totales" y no "netas": incluyen local y domicilio, y "netas"
  // sonaba a que las cortesias restaban (ronda 3).
  { metric: 'netRevenue', label: 'Ventas totales', from: (k) => k.netRevenue },
  {
    metric: 'averageTicket',
    label: 'Ticket promedio por comensal',
    from: (k) => k.averageTicketPerCustomer,
  },
  {
    metric: 'averageTicketPerTable',
    label: 'Ticket promedio por mesa',
    from: (k) => k.averageTicketPerTable,
  },
  {
    // Presupuesto maximo de compras del mes, en importe. Es distinto del
    // coste de materia prima consumida: aqui se compara lo COMPRADO.
    metric: 'materialPurchases',
    label: 'Compras de materia prima',
    from: (k) => k.materialCost,
  },
  { metric: 'foodCostPct', label: 'Costo de materia prima (%)', from: (k) => k.foodCostPct },
  { metric: 'laborCostPct', label: 'Costo de personal (%)', from: (k) => k.laborCostPct },
  { metric: 'primeCostPct', label: 'Prime Cost (%)', from: (k) => k.primeCostPct },
  // El objetivo de rentabilidad: aqui mas es mejor, al reves que los costes.
  { metric: 'ebitdaPct', label: 'EBITDA (%)', from: (k) => k.ebitdaPct },
  {
    metric: 'safetyMarginPct',
    label: 'Margen de seguridad (%)',
    from: (k) => k.safetyMarginPct,
  },
];

/**
 * Direccion de cada objetivo: en los costes y en el presupuesto de compras,
 * cumplir es quedarse EN o POR DEBAJO del objetivo; en el resto, en o por
 * encima. El semaforo y Alexia usan esta misma lista — si no la conocen,
 * marcan mal un objetivo (ronda 3).
 */
export const LOWER_IS_BETTER_TARGETS = new Set<string>([
  'materialPurchases',
  'foodCostPct',
  'laborCostPct',
  'primeCostPct',
]);

/** Objetivo contra real, como la parte superior de la hoja KPIs. */
export function compareTargets(
  input: FinanceInput,
  target: Target | undefined,
  month: string,
): TargetComparison[] {
  const kpis = computeKpis(input, periodOfMonth(month));
  const lowerIsBetter = LOWER_IS_BETTER_TARGETS;

  return TARGET_METRICS.map(({ metric, label, from }) => {
    const actual = from(kpis);
    const goal = target?.[metric] as number | undefined;
    if (goal == null) return { month, metric: String(metric), label, actual };
    const deviation = actual - goal;
    return {
      month,
      metric: String(metric),
      label,
      target: goal,
      actual,
      deviation: round(deviation, 2),
      deviationPct: goal !== 0 ? round((deviation / goal) * 100, 2) : 0,
      achieved: lowerIsBetter.has(String(metric)) ? actual <= goal : actual >= goal,
    };
  });
}

type TargetMetric = Exclude<keyof Target, 'tenantId' | 'month' | 'locationId' | 'salesMode' | 'monthlyRevenue' | 'sameForAllLocations'>;

/** Objetivos en importe: en el consolidado se suman. */
const ADDITIVE_TARGETS: TargetMetric[] = ['netRevenue', 'materialPurchases'];
/** Objetivos en porcentaje o por ticket: en el consolidado se ponderan por ventas. */
const WEIGHTED_TARGETS: TargetMetric[] = [
  'averageTicket',
  'averageTicketPerTable',
  'foodCostPct',
  'laborCostPct',
  'primeCostPct',
  'ebitdaPct',
  'safetyMarginPct',
];

/**
 * Objetivo consolidado de un grupo a partir de los objetivos de sus centros
 * (7 sep 2026). Nada se guarda: se compone al vuelo.
 *
 *  - Importes (ventas, compras): suma de los centros que lo han fijado.
 *  - Porcentajes y tickets: media ponderada por el objetivo de ventas de cada
 *    centro; si algún centro con ese objetivo no tiene objetivo de ventas, se
 *    cae a la media simple, que es lo unico honesto sin peso.
 *  - Una metrica que ningun centro ha fijado queda sin fijar.
 *
 * Con un solo objetivo devuelve ese mismo (sin `locationId`): asi el tenant
 * de un solo local sigue viendo exactamente lo que guardo.
 */
export function consolidateTargets(targets: Target[]): Target | undefined {
  if (targets.length === 0) return undefined;
  const { tenantId, month } = targets[0];
  if (targets.length === 1) {
    const { locationId: _locationId, ...single } = targets[0];
    return single;
  }
  const metrics: Partial<Record<TargetMetric, number>> = {};
  for (const metric of ADDITIVE_TARGETS) {
    const values = targets.map((t) => t[metric]).filter((v): v is number => v != null);
    if (values.length > 0) metrics[metric] = round(sum(values), 2);
  }
  for (const metric of WEIGHTED_TARGETS) {
    const withMetric = targets.filter((t) => t[metric] != null);
    if (withMetric.length === 0) continue;
    const weights = withMetric.map((t) => t.netRevenue);
    const weightable = weights.every((w): w is number => w != null && w > 0);
    const totalWeight = weightable ? sum(weights as number[]) : withMetric.length;
    const weighted = withMetric.reduce(
      (acc, t, i) => acc + (t[metric] as number) * (weightable ? (weights[i] as number) : 1),
      0,
    );
    metrics[metric] = round(weighted / totalWeight, 2);
  }
  return { tenantId, month, ...metrics };
}

/* ------------------------------------------------------------------ */
/* Gastos comunes del grupo                                            */
/* ------------------------------------------------------------------ */

export interface SharedAllocationInput {
  /** TODOS los gastos del tenant (propios de cada local y comunes). */
  expenses: ExpenseEntry[];
  /** TODAS las ventas del tenant, para el reparto por ventas. */
  revenues: DailyRevenue[];
  /** Local cuya cuenta se compone. */
  locationId: string;
  /** Locales activos del tenant, para el reparto a partes iguales. */
  activeLocationIds: string[];
  allocation?: SharedCostAllocation;
}

/**
 * Gastos que entran en la cuenta de UN local (7 sep 2026): los suyos enteros
 * mas la parte que le toca de cada gasto comun del grupo.
 *
 *  - `ventas`: proporcion de las ventas del local en el MES del gasto. Un mes
 *    sin ventas en ningun local cae al reparto del periodo entero, y sin
 *    ventas en absoluto, a partes iguales.
 *  - `fijo`: los porcentajes de Ajustes (locationId → %). Un local sin
 *    porcentaje no se lleva nada.
 *  - Sin ajuste: a partes iguales entre los locales activos.
 *
 * El gasto repartido conserva todo lo demas (fecha, proveedor, IVA, pago) y
 * lleva `allocatedPct` para que la pantalla pueda explicarlo. El consolidado
 * no pasa por aqui: alli el gasto comun entra entero.
 */
export function allocateSharedExpenses(input: SharedAllocationInput): ExpenseEntry[] {
  const { expenses, revenues, locationId, allocation } = input;
  const own = expenses.filter((e) => effectiveLocationId(e.locationId) === locationId);
  const shared = expenses.filter((e) => e.locationId === SHARED_LOCATION_ID);
  if (shared.length === 0) return own;

  const active = input.activeLocationIds.length > 0 ? input.activeLocationIds : [locationId];
  const equal = 1 / active.length;

  const fixedShare = (): number => {
    const pct = allocation?.shares?.[locationId];
    return pct == null ? 0 : pct / 100;
  };

  // Ventas por mes y local, y del periodo entero, calculadas una sola vez.
  const byMonth = new Map<string, { own: number; total: number }>();
  let periodOwn = 0;
  let periodTotal = 0;
  if (!allocation || allocation.method === 'ventas') {
    for (const day of revenues) {
      const amount = revenueOf(day);
      const month = monthOf(day.date);
      const bucket = byMonth.get(month) ?? { own: 0, total: 0 };
      bucket.total += amount;
      periodTotal += amount;
      if (effectiveLocationId(day.locationId) === locationId) {
        bucket.own += amount;
        periodOwn += amount;
      }
      byMonth.set(month, bucket);
    }
  }
  const salesShare = (date: string): number => {
    const bucket = byMonth.get(monthOf(date));
    if (bucket && bucket.total > 0) return bucket.own / bucket.total;
    if (periodTotal > 0) return periodOwn / periodTotal;
    return equal;
  };

  const allocated = shared.map((e) => {
    const share = allocation?.method === 'fijo' ? fixedShare() : salesShare(e.date);
    return {
      ...e,
      locationId,
      netAmount: round(e.netAmount * share, 2),
      allocatedPct: round(share * 100, 2),
    };
  });
  return [...own, ...allocated].sort((a, b) => a.date.localeCompare(b.date));
}

/* ------------------------------------------------------------------ */
/* Periodificacion de impuestos                                        */
/* ------------------------------------------------------------------ */

export type TaxBase = 'facturacion' | 'nomina';

export interface TaxAllocation {
  month: string;
  base: number;
  amount: number;
  sharePct: number;
}

/**
 * Reparte un pago de impuestos entre los meses que cubre, en proporcion a su
 * base de calculo. Sin esto, un pago trimestral hunde el mes en que se paga y
 * deja los otros dos artificialmente bien.
 *
 * Formula de la hoja "Calculadora de impuestos", celda D16:
 *   =$C$12 * (C16 / SUM($C$16:$C$27))     pago x base del mes / base total
 * donde C16 resuelve la base con XLOOKUP contra la hoja Tabs:
 *   "Sobre la facturacion" -> Tabs!J (INGRESOS del P&L)
 *   "Sobre la nomina"      -> Tabs!L (coste de personal = sueldos + SS)
 * La plantilla ofrece ademas "Sobre el beneficio / utilidad" (Tabs!M, EBITDA),
 * que aqui no se expone todavia.
 *
 * Con base total 0 Excel deja la celda vacia; nosotros repartimos a partes
 * iguales para no perder el importe del pago.
 */
export function allocateTaxPayment(
  input: FinanceInput,
  payment: number,
  months: string[],
  base: TaxBase,
): TaxAllocation[] {
  if (months.length === 0 || payment <= 0) return [];

  const bases = months.map((month) => {
    const period = periodOfMonth(month);
    if (base === 'facturacion') {
      return sum(input.revenues.filter((d) => inPeriod(d.date, period)).map(revenueOf));
    }
    return sum(
      input.expenses
        .filter((e) => inPeriod(e.date, period) && isLaborCategory(e.category))
        .map((e) => e.netAmount),
    );
  });

  const totalBase = sum(bases);

  // Si no hay base (mes sin actividad), se reparte a partes iguales.
  const allocations = months.map((month, i) => {
    const share = totalBase > 0 ? bases[i] / totalBase : 1 / months.length;
    return {
      month,
      base: round(bases[i], 2),
      amount: round(payment * share, 2),
      sharePct: round(share * 100, 2),
    };
  });

  // Redondear cada mes por separado puede dejar la suma unos centimos por
  // encima o por debajo del pago. El residuo se absorbe en el ultimo mes:
  // en una herramienta financiera, la suma del reparto CUADRA con el pago.
  const allocated = sum(allocations.map((a) => a.amount));
  const residual = round(payment - allocated, 2);
  if (residual !== 0 && allocations.length > 0) {
    const last = allocations[allocations.length - 1];
    last.amount = round(last.amount + residual, 2);
  }

  return allocations;
}

/* ------------------------------------------------------------------ */
/* Bloque R (fase Finanzas, 14 oct 2026): líneas del Estado de         */
/* resultados, repartos en meses cerrados y gastos fijos/variables.    */
/* ------------------------------------------------------------------ */

const ORDEN_GRUPOS: LineaResultado['grupo'][] = ['materia_prima', 'personal', 'generales', 'financieros'];

/**
 * Las líneas de cada grupo, en positivo: una por categoría de la plantilla,
 * una por categoría propia del usuario (hoy, el subgrupo del gasto) y, viendo
 * un local, «Gastos compartidos (tu parte)» con lo que le toca de los gastos
 * de todos. Dentro de cada grupo, de más a menos; compartidos al final.
 */
export function lineasDelResultado(expenses: ExpenseEntry[]): LineaResultado[] {
  const mapa = new Map<string, LineaResultado>();
  for (const e of expenses) {
    if (e.noEsGasto) continue;
    const categoria = visibleCategory(e.category);
    const grupo: LineaResultado['grupo'] | undefined =
      expenseGroupOf(categoria) ?? (BELOW_EBITDA_CATEGORIES.includes(categoria) ? 'financieros' : undefined);
    if (!grupo) continue; // amortizaciones: va con el desgaste de Inversiones
    const compartidos = e.allocatedPct != null && grupo !== 'financieros';
    const propia = !compartidos && grupo !== 'financieros' ? e.subgroup?.trim() || undefined : undefined;
    const clave = compartidos ? `${grupo}|*` : propia ? `${grupo}|+${propia}` : `${grupo}|${categoria}`;
    const linea: LineaResultado = mapa.get(clave)
      ?? { grupo, ...(compartidos ? { compartidos: true } : propia ? { propia } : { categoria }), importe: 0 };
    linea.importe = round(linea.importe + e.netAmount, 2);
    if (e.parteDe) {
      linea.repartos = [...(linea.repartos ?? []), { concepto: e.concept, n: e.parteDe.n, de: e.parteDe.de, importe: round(e.netAmount, 2) }];
    }
    mapa.set(clave, linea);
  }
  return [...mapa.values()]
    .filter((l) => l.importe !== 0)
    .sort((a, b) => {
      if (a.grupo !== b.grupo) return ORDEN_GRUPOS.indexOf(a.grupo) - ORDEN_GRUPOS.indexOf(b.grupo);
      if (!a.compartidos !== !b.compartidos) return a.compartidos ? 1 : -1;
      return b.importe - a.importe;
    });
}

const finDeMes = (month: string) => periodOfMonth(month).end;

/**
 * Partes de pagos repartidos que se decidieron con su mes ya cerrado (regla 5):
 * «Septiembre cambió el 17 oct: se le sumaron $1,231 del impuesto del 3er
 * trimestre». Una por pago.
 */
export function cambiosPorRepartos(expenses: ExpenseEntry[], period: Period): CambioPorReparto[] {
  const out = new Map<string, CambioPorReparto>();
  for (const e of expenses) {
    const decidido = e.reparto?.decididoEl;
    if (!e.parteDe || !decidido || !inPeriod(e.date, period)) continue;
    if (decidido <= finDeMes(monthOf(e.date))) continue;
    const previo = out.get(e.parteDe.entryId);
    out.set(e.parteDe.entryId, {
      fecha: decidido,
      concepto: e.concept,
      importe: round((previo?.importe ?? 0) + e.netAmount, 2),
    });
  }
  return [...out.values()].sort((a, b) => a.fecha.localeCompare(b.fecha));
}

/**
 * Gastos fijos y variables CON LAS CIFRAS DEL ESTADO DE RESULTADOS (regla 3
 * de Análisis): la materia prima entra por su consumo (compras ± recuento),
 * el desgaste sale de Inversiones y los intereses, del bloque financiero.
 * Fijos + variables = todo lo que se resta para llegar a la ganancia neta,
 * así que (ventas − equilibrio) × lo que deja cada peso = ganancia neta.
 */
export function fijosYVariables(pnl: ProfitAndLoss, settings?: AjusteNaturalezas): { fijos: number; variables: number } {
  let fijos = 0;
  let variables = 0;
  const poner = (naturaleza: CostNature, importe: number) => {
    if (naturaleza === 'fijo') fijos += importe; else variables += importe;
  };
  // Materia prima: variable, por su consumo (no por lo comprado).
  variables += pnl.materialConsumption;
  for (const [categoria, importe] of Object.entries(pnl.byCategory ?? {})) {
    const c = categoria as ExpenseCategory;
    if (!importe || isMaterialCategory(c) || c === 'amortizaciones' || c === 'intereses' || c === 'impuesto_ganancias') continue;
    poner(naturalezaDe(c, settings), importe);
  }
  poner(naturalezaDe('amortizaciones', settings), pnl.depreciation);
  poner(naturalezaDe('intereses', settings), pnl.financialExpenses);
  poner(naturalezaDe('impuesto_ganancias', settings), pnl.taxes);
  return { fijos: round(fijos, 2), variables: round(variables, 2) };
}
